import React, { useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import ThemeContext from '../contexts/ThemeContext';
import FunctionsContext from '../contexts/FunctionsContext';
import AppContext from '../contexts/AppContext';

import PrebookingHeader from '../components/PrebookingHeader';
import TranslationOrderStepIndicator from '../components/translationOrder/TranslationOrderStepIndicator';
import useTranslationOrderState from '../hooks/useTranslationOrderState';
import useFileTransferProgress from '../hooks/useFileTransferProgress';
import TranslationOrderDetailsStep from '../components/translationOrder/TranslationOrderDetailsStep';
import TranslationOrderInvoicingStep from '../components/translationOrder/TranslationOrderInvoicingStep';
import TranslationOrderSummaryStep from '../components/translationOrder/TranslationOrderSummaryStep';
import ContractSelectionModal from '../components/ContractSelectionModal';
import {
  ButtonContainer,
  BackButton,
  NextButton,
} from '../components/PrebookingSharedComponents';


import i18n from '../i18n';
import { createTranslationOrder, cancelTranslationOrder } from '../services/translationOrderService';
import {
  getAttachmentUploadUrl,
  uploadFileToS3,
  confirmAttachmentUpload,
  MULTIPART_THRESHOLD,
} from '../services/prebookService';
import { uploadFileMultipart } from '../services/multipartUploadService';
import { showError } from '../services/toastService';
import { PATH_ROUTES } from '../utils/pathRoutes';
import {
  contractAllowsTranslation,
  getActiveContractsFromUser,
  getContractById,
  getTranslationOrderableContractsFromUser,
  hasTranslationCapableContract,
  shouldBlockOrdering,
  shouldBlockTranslationOrdering,
} from '../utils/contractUtils';
import { canAccessTranslations } from '../utils/translationAccess';

const StyledWizard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
`;

const WizardContainer = styled.div`
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
`;

const WizardContent = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 600;
  color: ${({ colors }) => colors.twilight};
  margin: 0;
`;

const StepContent = styled.div`
  margin-top: 32px;
`;

const ConfirmationButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: flex-end;
`;

const CancelOrderButton = styled.button`
  padding: 12px 32px;
  border-radius: 25px;
  border: none;
  background: ${({ colors }) => colors.heroPurple};
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { opacity: 0.8; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const TranslationOrderWizard = () => {
  const { colors } = useContext(ThemeContext);
  const { setBusy } = useContext(FunctionsContext);
  const { user } = useContext(AppContext);
  const history = useHistory();
  const location = useLocation();
  const translationEnabled = canAccessTranslations(user);

  const referenceChips = useMemo(() => {
    const chips = [];
    if (user?.invoiceReference1) chips.push(user.invoiceReference1);
    if (user?.invoiceReference2) chips.push(user.invoiceReference2);
    if (user?.userGroup?.invoiceReference) chips.push(user.userGroup.invoiceReference);
    return chips;
  }, [user]);

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [showContractSelection, setShowContractSelection] = useState(false);
  const orderState = useTranslationOrderState();
  const uploadProgress = useFileTransferProgress();
  const { formData, updateFields } = orderState;
  const selectedContractId = formData.selectedContractId;
  const selectedContractName = formData.selectedContractName;
  const activeContracts = useMemo(() => getActiveContractsFromUser(user), [user]);
  // TUL-5355: only contracts whose interpretation types allow translations are offered here.
  const orderableContracts = useMemo(() => getTranslationOrderableContractsFromUser(user), [user]);
  const orderingBlockedRef = useRef(false);

  const handleClose = useCallback(() => {
    orderState.resetForm();
    history.replace(location.state?.returnTo || PATH_ROUTES.translations);
  }, [history, location.state, orderState]);

  useEffect(() => {
    if (!translationEnabled) {
      setShowContractSelection(false);
      history.replace(location.state?.returnTo || PATH_ROUTES.home);
      return;
    }

    if (shouldBlockOrdering(user)) {
      setShowContractSelection(false);
      if (!orderingBlockedRef.current) {
        orderingBlockedRef.current = true;
        showError(i18n('no_orderable_contract', 'No contract is available for your languages. Please contact support.'), 'no-orderable-contract');
        handleClose();
      }
      return;
    }

    // TUL-5355: active contracts exist but none of them is usable for a translation order. A
    // genuine capability gap gets the translation message; translation-capable contracts that
    // were narrowed away by the hero target-language filter get the language message instead.
    if (shouldBlockTranslationOrdering(user)) {
      setShowContractSelection(false);
      if (!orderingBlockedRef.current) {
        orderingBlockedRef.current = true;
        const capabilityGap = !hasTranslationCapableContract(user);
        showError(
          capabilityGap
            ? i18n('no_translation_contract', 'None of your contracts allow translation orders. Please contact support.')
            : i18n('no_orderable_contract', 'No contract is available for your languages. Please contact support.'),
          capabilityGap ? 'no-translation-contract' : 'no-orderable-contract'
        );
        handleClose();
      }
      return;
    }

    const updateContractIfChanged = (contractId, contractName) => {
      if (selectedContractId === contractId && selectedContractName === contractName) {
        return false;
      }
      updateFields({
        selectedContractId: contractId,
        selectedContractName: contractName,
      });
      return true;
    };

    const routeContractId = location?.state?.selectedContractId;
    const routeContract = getContractById(activeContracts, routeContractId);

    // TUL-5355: a contract handed over via navigation state must be one of the user's active
    // contracts and allow translations; anything else (unknown, inactive or translation-disabled)
    // falls through to the filtered picker logic below.
    if (routeContract && contractAllowsTranslation(routeContract)) {
      updateContractIfChanged(routeContract.id, routeContract.name);
      setShowContractSelection(false);
      return;
    }

    if (orderableContracts.length === 1) {
      updateContractIfChanged(orderableContracts[0].id, orderableContracts[0].name);
      setShowContractSelection(false);
      return;
    }

    if (orderableContracts.length > 1 && !selectedContractId) {
      setShowContractSelection(true);
    }
  }, [
    activeContracts,
    orderableContracts,
    translationEnabled,
    user,
    history,
    handleClose,
    location.state,
    location?.state?.selectedContractId,
    selectedContractId,
    selectedContractName,
    updateFields,
  ]);

  const handleContractSelected = useCallback((contract) => {
    updateFields({
      selectedContractId: contract.id,
      selectedContractName: contract.name,
    });
    setShowContractSelection(false);
  }, [updateFields]);

  const step1Ref = useRef(null);
  const step2Ref = useRef(null);

  const handleNext = useCallback(() => {
    if (currentStep === 1 && step1Ref.current?.validate) {
      if (!step1Ref.current.validate()) {
        return;
      }
    }
    if (currentStep === 2 && step2Ref.current?.validate) {
      if (!step2Ref.current.validate()) {
        return;
      }
    }
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setBusy(true);

    let prebookingId;
    try {
      const result = await createTranslationOrder({
        contractId: orderState.formData.selectedContractId,
        fromLanguageId: orderState.formData.fromLanguageId,
        toLanguageId: orderState.formData.toLanguageId,
        instructionsForTranslator: orderState.formData.instructionsForTranslator,
        translatorLevelIds: orderState.formData.translatorLevelIds,
        desiredDeliveryDate: orderState.formData.desiredDeliveryDate?.getTime() || null,
        externalOrderId: orderState.formData.externalOrderId,
        additionalInvoicingInfo: orderState.formData.additionalInvoicingInfo,
        ccEmails: orderState.formData.ccEmails,
        invoiceUserGroupId: orderState.formData.costAllocation !== 'other'
          ? orderState.formData.costAllocation
          : (orderState.formData.costAllocationGroup?.id || null),
      });

      prebookingId = Array.isArray(result) ? result[0] : result?._id;
      if (!prebookingId) {
        throw new Error('Failed to get order ID from response');
      }
    } catch (error) {
      console.error('Failed to create translation order:', error);
      showError(error.message || i18n('translation_order_submit_failed'));
      setIsSubmitting(false);
      setBusy(false);
      return;
    }

    // Order created — drop the global overlay so the per-file upload bars show.
    setBusy(false);

    try {
      // Upload attachments in parallel after order is created
      if (orderState.formData.attachments?.length > 0) {
        const SECONDS_PER_MINUTE = 60;
        const uploadResults = await Promise.allSettled(
          orderState.formData.attachments.map(async (attachment) => {
            const file = orderState.getFile(attachment.id);
            if (!file) {
              uploadProgress.fail(attachment.id);
              throw new Error(`File not found for attachment: ${attachment.filename}`);
            }
            const unitScope = attachment.estimatedScope
              ? (attachment.scopeType === 'seconds' ? Number(attachment.estimatedScope) * SECONDS_PER_MINUTE : Number(attachment.estimatedScope))
              : null;
            const unitType = attachment.scopeType || null;

            uploadProgress.start(attachment.id, file.size);
            const onProgress = (loaded, total) => uploadProgress.update(attachment.id, loaded, total || file.size);

            try {
              if (file.size > MULTIPART_THRESHOLD) {
                // Large source files use resumable S3 multipart upload, which internally
                // initiates + uploads all parts + completes (no separate presign/confirm).
                await uploadFileMultipart({ prebookingId, file, unitScope, unitType, onProgress });
              } else {
                const { uploadUrl, attachmentId } = await getAttachmentUploadUrl(
                  prebookingId,
                  attachment.filename,
                  file.size,
                  attachment.contentType,
                  unitScope,
                  unitType,
                );
                await uploadFileToS3(uploadUrl, file, { onProgress });
                await confirmAttachmentUpload(prebookingId, attachmentId);
              }
              uploadProgress.complete(attachment.id);
            } catch (e) {
              uploadProgress.fail(attachment.id);
              throw e;
            }
          })
        );

        const failedUploads = uploadResults.filter(r => r.status === 'rejected');
        if (failedUploads.length > 0) {
          failedUploads.forEach(r => console.error('Failed to upload attachment:', r.reason));
          showError(
            i18n('translation_order_attachment_upload_failed') +
            ` (${failedUploads.length}/${orderState.formData.attachments.length})`
          );
        }
      }

      setCreatedOrderId(prebookingId);
      setCurrentStep(4);
    } catch (error) {
      console.error('Failed to upload translation attachments:', error);
      showError(error.message || i18n('translation_order_submit_failed'));
    } finally {
      setIsSubmitting(false);
    }
  }, [orderState, setBusy, uploadProgress]);

  const handleCancelOrder = useCallback(async () => {
    if (!createdOrderId) return;

    setIsSubmitting(true);
    setBusy(true);
    try {
      await cancelTranslationOrder(createdOrderId);
      orderState.resetForm();
      history.replace(location.state?.returnTo || PATH_ROUTES.translations);
    } catch (error) {
      console.error('Failed to cancel translation order:', error);
      showError(error.message || i18n('translation_order_cancel_failed'));
    } finally {
      setIsSubmitting(false);
      setBusy(false);
    }
  }, [createdOrderId, history, location.state, orderState, setBusy]);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <TranslationOrderDetailsStep
            ref={step1Ref}
            formData={orderState.formData}
            updateField={orderState.updateField}
            addAttachment={orderState.addAttachment}
            removeAttachment={orderState.removeAttachment}
            updateAttachmentScope={orderState.updateAttachmentScope}
          />
        );
      case 2:
        return (
          <TranslationOrderInvoicingStep
            ref={step2Ref}
            formData={orderState.formData}
            updateField={orderState.updateField}
            addEmail={orderState.addEmail}
            removeEmail={orderState.removeEmail}
            referenceChips={referenceChips}
          />
        );
      case 3:
        return (
          <TranslationOrderSummaryStep
            formData={orderState.formData}
            getFile={orderState.getFile}
            uploadProgress={uploadProgress.progress}
          />
        );
      case 4:
        return (
          <TranslationOrderSummaryStep
            formData={orderState.formData}
            getFile={orderState.getFile}
            uploadProgress={uploadProgress.progress}
            isConfirmation
          />
        );
      default:
        return null;
    }
  };

  const renderButtons = () => {
    // Step 4: Confirmation view
    if (currentStep === 4) {
      return (
        <ConfirmationButtons>
          <CancelOrderButton colors={colors} onClick={handleCancelOrder} disabled={isSubmitting}>
            {i18n('translation_order_cancel_order')}
          </CancelOrderButton>
        </ConfirmationButtons>
      );
    }

    // Step 1: Only Next
    if (currentStep === 1) {
      return (
        <ButtonContainer>
          <NextButton onClick={handleNext}>
            {i18n('next')}
          </NextButton>
        </ButtonContainer>
      );
    }

    // Steps 2-3: Back + Next/Confirm. Once submission starts, hide Back so the
    // user can't navigate away mid-upload (and Next right-aligns on its own).
    const isLastStep = currentStep === 3;
    const showBackButton = !isSubmitting;
    return (
      <ButtonContainer hasBackButton={showBackButton}>
        {showBackButton && (
          <BackButton onClick={handleBack}>
            {i18n('back')}
          </BackButton>
        )}
        <NextButton
          onClick={isLastStep ? handleSubmit : handleNext}
          disabled={isLastStep && isSubmitting}
        >
          {isLastStep
            ? (isSubmitting ? i18n('submitting') : i18n('confirm_order'))
            : i18n('next')}
        </NextButton>
      </ButtonContainer>
    );
  };

  if (!translationEnabled) {
    return null;
  }

  return (
    <StyledWizard>
      <WizardContainer>
        <PrebookingHeader onCancel={handleClose} closeMode={currentStep === 4} />

        <WizardContent>
          <Header>
            <Title colors={colors}>
              {currentStep === 4
                ? i18n('translation_order_confirmation')
                : i18n('translation_order_title')}
            </Title>
          </Header>

          {currentStep < 4 && (
            <TranslationOrderStepIndicator currentStep={currentStep} />
          )}

          <StepContent>
            {renderStep()}
          </StepContent>

          {renderButtons()}
        </WizardContent>
      </WizardContainer>

      <ContractSelectionModal
        isOpen={showContractSelection}
        contracts={orderableContracts.map(({ id, name }) => ({ id, name }))}
        onSelect={handleContractSelected}
        onClose={handleClose}
        colors={colors}
      />
    </StyledWizard>
  );
};

export default TranslationOrderWizard;
